import {
  Bookmark as BookmarkIcon,
  X as CloseIcon,
  LibraryBig as LibraryAddIcon,
  Folder as FolderIcon,
  Star as StarRoundedIcon,
} from 'lucide-react';
import {
  Typography,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Chip,
} from '@mui/material';
import { useState, useCallback, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Prompt } from '../../api/model';
import type { PromptTemplate, PromptTemplateCaseListing } from '../../../prompthub/api';
import CaseTemplatePickerModal from '../CaseTemplatePickerModal';
import styles from './Sidebar.module.scss';

type SidebarTab = 'saved' | 'favorites' | 'cases';

interface SidebarProps {
  onPromptChoose: (content: string) => void;
  prompts: Prompt[];
  onDelete: (id: number) => void;
  loading: boolean;
  cases?: PromptTemplateCaseListing[];
  casesLoading?: boolean;
  favoriteTemplates?: PromptTemplate[];
  favoritesLoading?: boolean;
}

export default function Sidebar({
  onPromptChoose,
  prompts,
  onDelete,
  loading,
  cases = [],
  casesLoading = false,
  favoriteTemplates = [],
  favoritesLoading = false,
}: SidebarProps) {
  const [activeTab, setActiveTab] = useState<SidebarTab>('saved');
  const [selectedChip, setSelectedChip] = useState<Prompt | null>(null);
  const [selectedFavoriteId, setSelectedFavoriteId] = useState<string | null>(null);
  const [casePickerOpen, setCasePickerOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState<PromptTemplateCaseListing | null>(null);

  const handleTabChange = useCallback((tab: SidebarTab) => {
    setActiveTab(tab);
    setSelectedChip(null);
    setSelectedFavoriteId(null);
  }, []);

  const handleCaseClick = (caseItem: PromptTemplateCaseListing) => {
    setSelectedCase(caseItem);
    setCasePickerOpen(true);
  };

  const handleTemplateSelect = (content: string) => {
    onPromptChoose(content);
    setCasePickerOpen(false);
    setSelectedCase(null);
  };

  const handleCasePickerClose = () => {
    setCasePickerOpen(false);
    setSelectedCase(null);
  };

  const handleChipClick = (prompt: Prompt) => {
    if (selectedChip === prompt) setSelectedChip(null);
    else {
      setSelectedChip(prompt);
      onPromptChoose(prompt.content);
    }
  };

  const handleFavoriteClick = (template: PromptTemplate) => {
    if (selectedFavoriteId === template.id) {
      setSelectedFavoriteId(null);
    } else {
      setSelectedFavoriteId(template.id);
      onPromptChoose(template.content);
    }
  };

  const handleDelete = (e: React.MouseEvent, prompt: Prompt) => {
    e.stopPropagation();
    onDelete(prompt.id);
  };

  const tabs: { id: SidebarTab; label: ReactNode; ariaLabel: string }[] = [
    {
      id: 'saved',
      label: (
        <>
          Сохран
          <wbr />
          ённые
        </>
      ),
      ariaLabel: 'Сохранённые',
    },
    {
      id: 'favorites',
      label: (
        <>
          Избран
          <wbr />
          ные
        </>
      ),
      ariaLabel: 'Избранные',
    },
    { id: 'cases', label: 'Кейсы', ariaLabel: 'Кейсы' },
  ];

  return (
    <div className={styles.root}>
      <Button
        component={Link}
        to="/prompthub"
        variant="outlined"
        size="small"
        fullWidth
        startIcon={<LibraryAddIcon size={14} />}
        className={styles.storeLink}
      >
        Хранилище промптов
      </Button>

      <div className={styles.tabBar} role="tablist" aria-label="Разделы промптов">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
            aria-label={t.ariaLabel}
            className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
            onClick={() => handleTabChange(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className={styles.tabPanel} role="tabpanel">
        {activeTab === 'saved' && (
          <div className={styles.promptsContainer}>
            {loading ? (
              <div className={styles.loadingWrap}>
                <CircularProgress size={20} />
              </div>
            ) : prompts?.length > 0 ? (
              prompts.map((prompt) => (
                <Card
                  key={prompt.id}
                  className={`${styles.promptCard} ${selectedChip === prompt ? styles.selected : ''}`}
                  onClick={() => handleChipClick(prompt)}
                  elevation={0}
                  sx={{ boxShadow: 'none' }}
                >
                  <CardContent className={styles.cardContent}>
                    <Tooltip
                      title={prompt.content}
                      placement="left"
                      enterDelay={400}
                      componentsProps={{
                        tooltip: { sx: { maxWidth: 320 } },
                      }}
                    >
                      <Typography component="div" className={styles.promptText}>
                        {prompt.content}
                      </Typography>
                    </Tooltip>
                    <div className={styles.cardActions}>
                      <Tooltip title="Удалить промпт" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => handleDelete(e, prompt)}
                          className={styles.deleteBtn}
                          aria-label="Удалить промпт"
                        >
                          <CloseIcon size={14} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className={styles.emptyWrap}>
                <BookmarkIcon size={28} className={styles.emptyIcon} />
                <Typography variant="body2" sx={{ fontSize: '13px', mb: 0.5 }}>
                  Нет сохранённых промптов
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '12px', color: 'inherit', opacity: 0.8 }}
                >
                  Сохраняйте полезные промпты для быстрого доступа
                </Typography>
              </div>
            )}
          </div>
        )}

        {activeTab === 'favorites' && (
          <div className={styles.promptsContainer}>
            {favoritesLoading ? (
              <div className={styles.loadingWrap}>
                <CircularProgress size={20} />
              </div>
            ) : favoriteTemplates.length > 0 ? (
              favoriteTemplates.map((template) => (
                <Card
                  key={template.id}
                  className={`${styles.promptCard} ${styles.favoriteCard} ${
                    selectedFavoriteId === template.id ? styles.selected : ''
                  }`}
                  onClick={() => handleFavoriteClick(template)}
                  elevation={0}
                  sx={{ boxShadow: 'none' }}
                >
                  <CardContent className={styles.cardContentFavorite}>
                    <div className={styles.favoriteCardHead}>
                      <StarRoundedIcon
                        size={13}
                        fill="currentColor"
                        className={styles.favoriteStar}
                      />
                      <Typography component="div" className={styles.favoriteTitle} noWrap>
                        {template.title}
                      </Typography>
                    </div>
                    <Tooltip
                      title={template.content}
                      placement="left"
                      enterDelay={400}
                      componentsProps={{
                        tooltip: { sx: { maxWidth: 320 } },
                      }}
                    >
                      <Typography component="div" className={styles.promptText}>
                        {template.content}
                      </Typography>
                    </Tooltip>
                  </CardContent>
                </Card>
              ))
            ) : (
              <div className={styles.emptyWrap}>
                <StarRoundedIcon size={28} className={styles.emptyIcon} />
                <Typography variant="body2" sx={{ fontSize: '13px', mb: 0.5 }}>
                  Нет избранных шаблонов
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '12px', color: 'inherit', opacity: 0.8 }}
                >
                  Отметьте звёздочкой шаблоны в хранилище промптов
                </Typography>
              </div>
            )}
          </div>
        )}

        {activeTab === 'cases' && (
          <div className={styles.promptsContainer}>
            {casesLoading ? (
              <div className={styles.loadingWrap}>
                <CircularProgress size={20} />
              </div>
            ) : cases.length > 0 ? (
              <div className={styles.casesList}>
                {cases.map((caseItem) => (
                  <Chip
                    key={caseItem.id}
                    icon={<FolderIcon size={12} className={styles.caseChipIcon} />}
                    label={caseItem.title}
                    onClick={() => handleCaseClick(caseItem)}
                    size="small"
                    variant="outlined"
                    className={styles.caseChip}
                    sx={{ boxShadow: 'none' }}
                  />
                ))}
              </div>
            ) : (
              <div className={styles.emptyWrap}>
                <FolderIcon size={28} className={styles.emptyIcon} />
                <Typography variant="body2" sx={{ fontSize: '13px', mb: 0.5 }}>
                  Нет доступных кейсов
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '12px', color: 'inherit', opacity: 0.8 }}
                >
                  Кейсы появятся, когда для вашей роли их настроят
                </Typography>
              </div>
            )}
          </div>
        )}
      </div>

      <CaseTemplatePickerModal
        open={casePickerOpen}
        onClose={handleCasePickerClose}
        caseId={selectedCase?.id ?? null}
        caseTitle={selectedCase?.title ?? ''}
        onSelect={handleTemplateSelect}
      />
    </div>
  );
}
